import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../i18n/useTranslation';
import { Button } from './Button';
import { LanguageSwitcher } from './LanguageSwitcher';
import './HeaderBar.css';

/**
 * HeaderBar - Global header with brand, theme toggle, and auth buttons
 */
export const HeaderBar = ({ className = '' }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, authData, logout, pendingRequestsCount, newConfirmationsCount } = useApp();
  const { language, changeLanguage } = useLanguage();
  const { t } = useTranslation();

  const handleLogout = async () => {
    await logout();
    setMobileMenuOpen(false);
    navigate('/');
  };

  const handleNavigate = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  // Inline styles to guarantee horizontal layout
  const rowStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px'
  };

  return (
    <header className={`header-bar ${className}`.trim()}>
      <div className="header-bar-container" style={rowStyle}>
        {/* Left: Brand Logo */}
        <div 
          className="header-brand" 
          onClick={() => handleNavigate('/')} 
          style={{ cursor: 'pointer', flexShrink: 0 }}
        >
          <img 
            src="/images/trempi-logo.png" 
            alt="Trempi" 
            className="header-logo-img"
          />
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }}></div>

        {/* Right: Desktop Actions */}
        <div className="header-desktop" style={rowStyle}>
          {/* Language Switcher */}
          <LanguageSwitcher />
          
          {isAuthenticated ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate('/my-account')}>
                {t('header.myEvents')}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate('/my-rides')} style={{ position: 'relative' }}>
                {t('header.myRides')}
                {(pendingRequestsCount > 0 || newConfirmationsCount > 0) && (
                  <span style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    background: '#EF4444',
                    color: 'white',
                    borderRadius: '50%',
                    width: '10px',
                    height: '10px',
                    minWidth: '10px',
                    display: 'block',
                    boxShadow: '0 0 0 2px white'
                  }} title={`${pendingRequestsCount} pending requests, ${newConfirmationsCount} new confirmations`}></span>
                )}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate('/my-account')}>
                {authData?.name?.split(' ')[0] || t('header.account')}
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                {t('header.logout')}
              </Button>
            </>
          ) : (
            <Button variant="secondary" size="sm" onClick={() => navigate('/login')}>
              {t('header.loginSignUp')}
            </Button>
          )}
        </div>

        {/* Right: Mobile Actions - Language in header + hamburger */}
        <div className="header-mobile" style={rowStyle}>
          <LanguageSwitcher className="header-mobile-lang" />
          <button 
            className={`hamburger-btn ${mobileMenuOpen ? 'active' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="mobile-menu">
          <div className="mobile-menu-content">
            {isAuthenticated ? (
              <>
                <div className="mobile-menu-user">
                  <div className="mobile-user-avatar">
                    {authData?.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="mobile-user-info">
                    <span className="mobile-user-name">{authData?.name || 'User'}</span>
                    <span className="mobile-user-phone">{authData?.phone}</span>
                  </div>
                </div>
                {/* Language Switcher in Mobile Menu */}
                <div className="mobile-menu-divider"></div>
                <div className="mobile-language-switcher">
                  <button
                    className={`mobile-lang-btn ${language === 'en' ? 'active' : ''}`}
                    onClick={() => changeLanguage('en')}
                  >
                    English
                  </button>
                  <button
                    className={`mobile-lang-btn ${language === 'ar' ? 'active' : ''}`}
                    onClick={() => changeLanguage('ar')}
                  >
                    العربية
                  </button>
                </div>
                <div className="mobile-menu-divider"></div>
                <button className="mobile-menu-item" onClick={() => handleNavigate('/my-account')}>
                  📅 {t('header.myEvents')}
                </button>
                <button className="mobile-menu-item" onClick={() => handleNavigate('/my-rides')} style={{ position: 'relative' }}>
                  🚗 {t('header.myRides')}
                  {(pendingRequestsCount > 0 || newConfirmationsCount > 0) && (
                    <span style={{
                      position: 'absolute',
                      top: '8px',
                      right: '12px',
                      background: '#EF4444',
                      borderRadius: '50%',
                      width: '10px',
                      height: '10px',
                      minWidth: '10px',
                      display: 'block',
                      boxShadow: '0 0 0 2px white'
                    }} title={`${pendingRequestsCount} pending requests, ${newConfirmationsCount} new confirmations`}></span>
                  )}
                </button>
                <button className="mobile-menu-item" onClick={() => handleNavigate('/my-account')}>
                  👤 {t('header.account')}
                </button>
                <button className="mobile-menu-item" onClick={() => handleNavigate('/create-event')}>
                  ✨ {t('header.createEvent')}
                </button>
                <div className="mobile-menu-divider"></div>
                <button className="mobile-menu-item mobile-menu-logout" onClick={handleLogout}>
                  🚪 {t('header.logout')}
                </button>
              </>
            ) : (
              <>
                {/* Language Switcher in Mobile Menu */}
                <div className="mobile-language-switcher">
                  <button
                    className={`mobile-lang-btn ${language === 'en' ? 'active' : ''}`}
                    onClick={() => changeLanguage('en')}
                  >
                    English
                  </button>
                  <button
                    className={`mobile-lang-btn ${language === 'ar' ? 'active' : ''}`}
                    onClick={() => changeLanguage('ar')}
                  >
                    العربية
                  </button>
                </div>
                <div className="mobile-menu-divider"></div>
                <button className="mobile-menu-item" onClick={() => handleNavigate('/login')}>
                  📱 {t('header.loginSignUp')}
                </button>
                <button className="mobile-menu-item" onClick={() => handleNavigate('/create-event')}>
                  📅 {t('header.createEvent')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default HeaderBar;
